import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsArticles } from "@/mocks/news";
import { liveSettings } from "@/mocks/settings";
import { useTranslatedList } from "@/hooks/useTranslatedList";

export default function HeroSection() {
  const { t } = useTranslation();
  const translatedArticles = useTranslatedList(newsArticles, ["headline"]);
  const featuredArticle = translatedArticles.find((a) => a.featured) || translatedArticles[0];

  return (
    <section className="relative min-h-[90vh] md:min-h-[85vh] flex items-center overflow-hidden bg-primary-900">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20modern%20television%20broadcast%20studio%20with%20large%20glowing%20LED%20screens%2C%20sleek%20anchor%20desk%2C%20dramatic%20cinematic%20studio%20lighting%20in%20deep%20red%20and%20orange%20accents%20against%20dark%20charcoal%20tones%2C%20polished%20reflective%20floor%2C%20high-end%20media%20production%20facility%2C%20editorial%20broadcast%20photography%2C%20crisp%20high-contrast%20composition%2C%20futuristic%20and%20prestigious%20atmosphere&width=1600&height=900&seq=hero-bg&orientation=landscape&nocache=true"
          alt="L+TV Broadcast Studio"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/80 via-primary-900/70 to-primary-900/90"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 ltv-container py-24 md:py-32 mt-16">
        <div className="max-w-3xl">
          {/* Logo */}
          <div className="mb-6">
            <img
              src="https://storage.helloreaddy.io/project_files/f3582b29-89ce-4104-acc4-d71527c07beb/9c1ed9ab-5b65-494b-810b-15bf34b3d622_compressed_IMG-20260825-WA0008.webp"
              alt="L+TV TÉLÉVISION"
              className="h-16 md:h-20 w-auto object-contain"
            />
          </div>

          {/* Tagline */}
          <p className="text-accent-400 text-sm md:text-base font-semibold uppercase tracking-widest mb-4">
            {t("home.tagline")}
          </p>

          {/* Headline */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            {featuredArticle.headline}
          </h1>

          {/* Introduction */}
          <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
            {t("home.intro")}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <Link
              to="/live"
              className="inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-white font-bold px-6 py-3.5 rounded-md transition-all text-sm md:text-base whitespace-nowrap"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
              </span>
              {t("home.watchLive")}
            </Link>
            <Link
              to="/videos"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 font-semibold px-6 py-3.5 rounded-md transition-all text-sm md:text-base whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              {t("home.watchVideos")}
            </Link>
          </div>

          {/* Live indicator */}
          {liveSettings.isLive && (
            <div className="mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-md">
              <span className="ltv-live-badge">LIVE</span>
              <span className="text-white/80 text-sm">{liveSettings.currentProgram}</span>
            </div>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 text-white/40">
        <span className="text-xs uppercase tracking-widest">{t("home.scroll")}</span>
        <i className="ri-arrow-down-line animate-bounce text-lg"></i>
      </div>
    </section>
  );
}